import React from 'react';
import { useAppSelector } from 'hooks';

import { AuthTags } from '~constants/authTags';

import { gocardlessPaymentUrl } from '~direct-debits/lib/gocardlessPaymentUrl';

import { ClickableIcon } from '~components';
import { faArrowUpRightFromSquare, faBan, faFileInvoiceDollar, faMoneyBill } from '~components/icon';

import { PaymentBatchClient } from '~direct-debits/models';

import { selectUserTags } from '~main/selectors/login';

interface ComponentProps {
  row: PaymentBatchClient;
  onViewInvoices?: (clientId: number) => void;
  onRefund?: (row: PaymentBatchClient) => void;
  onReverse?: (row: PaymentBatchClient) => void;
  environment?: string | null;
}

// A collected payment can be refunded; an in-flight one reversed (cancelled).
// Mutually exclusive — the line's status picks which (if either) appears.
const REFUNDABLE_TAG = 'paid_out';
const REVERSIBLE_TAGS = new Set(['submitted', 'pending_customer_approval']);

const BatchClientRowAction: React.FC<ComponentProps> = ({
  row,
  onViewInvoices,
  onRefund,
  onReverse,
  environment,
}) => {
  const userTags = useAppSelector(selectUserTags);
  const canRefund = userTags.includes(AuthTags.DIRECTDEBITS.REFUND);
  const canReverse = userTags.includes(AuthTags.DIRECTDEBITS.REVERSE);

  const statusTag = row.payment_status?.tag ?? null;
  const hasLine = Boolean(row.payment_batch_item_id);

  const showRefund = hasLine && canRefund && !row.refund && statusTag === REFUNDABLE_TAG && Boolean(onRefund);
  const showReverse =
    hasLine && canReverse && statusTag !== null && REVERSIBLE_TAGS.has(statusTag) && Boolean(onReverse);

  // The invoices open in a dialog over the batch detail, so this icon reads as
  // a plain "open" control - there is no inline expanded state to report.
  const viewDisabled = !onViewInvoices || row.invoice_count === 0;

  // GoCardless dashboard deep-link; null (icon hidden) when unsubmitted or env unknown.
  const gocardlessUrl = gocardlessPaymentUrl(row.gocardless_payment_id, environment);
  const openInGoCardless = () => {
    if (gocardlessUrl) window.open(gocardlessUrl, '_blank', 'noopener,noreferrer');
  };

  return (
    <div className="d-flex gap-2 justify-content-end align-items-center">
      {showRefund && (
        <ClickableIcon
          id={`dd-payment-batch-refund-${row.client_id}`}
          dataCy={`dd-payment-batch-refund-${row.client_id}`}
          icon={faMoneyBill}
          color="warning"
          toolTip="Refund"
          ariaLabel="Refund"
          onClick={() => onRefund?.(row)}
        />
      )}
      {showReverse && (
        <ClickableIcon
          id={`dd-payment-batch-reverse-${row.client_id}`}
          dataCy={`dd-payment-batch-reverse-${row.client_id}`}
          icon={faBan}
          color="danger"
          toolTip="Reverse"
          ariaLabel="Reverse"
          onClick={() => onReverse?.(row)}
        />
      )}
      <ClickableIcon
        id={`dd-payment-batch-client-view-${row.client_id}`}
        dataCy={`dd-payment-batch-client-view-${row.client_id}`}
        icon={faFileInvoiceDollar}
        toolTip={viewDisabled ? 'No invoices to view' : 'View Invoices'}
        ariaLabel="View Invoices"
        showTooltipWhenDisabled
        disabled={viewDisabled}
        onClick={() => onViewInvoices?.(row.client_id)}
      />
      {gocardlessUrl && (
        <ClickableIcon
          id={`dd-payment-batch-gocardless-link-${row.client_id}`}
          dataCy={`dd-payment-batch-gocardless-link-${row.client_id}`}
          icon={faArrowUpRightFromSquare}
          toolTip="Open GoCardless"
          ariaLabel="Open GoCardless"
          onClick={openInGoCardless}
        />
      )}
    </div>
  );
};

export default BatchClientRowAction;
